import React, { useEffect, useState } from "react";
import { useHistory, useRouteMatch } from "react-router";
import { isEmpty, uniqueId } from "lodash";
import moment from "moment";
import { v4 as uuid } from "uuid";
import { Button, Col, Form, Input, Row, Select, Table, Tooltip } from "antd";
import {
  ServiceListFilterValues,
  ServiceListFilterValuesChange,
} from "../../containers/AccountProductsContainer/ProductListContainer/ProductListContainerInterface";
import dots from "../../img/3dots.svg";
import { checkIsLoaded, checkIsLoading, sortByToDate, toHttpFormat } from "../../lib/utils";
import { useAppSelector } from "../..";
import { searchIconForInput } from "../../lib/icons";
import documentList from "../../img/documentList.svg";
import { IProduct } from "../../lib/types";
import { IProductsTable, IProductsVersionsTable } from "./ProductsTableInterface";
import { TableColumn } from "../../lib/interfaces";
import ToggleColumns from "../ToggleableVersionsTable/ToggleColumns";
import { ReactComponent as VersionsHiddenIcon } from "../../img/versions_icons/versionsHidden_icon.svg";
import { ReactComponent as VersionsShownIcon } from "../../img/versions_icons/versionsShown_icon.svg";
import { getBlockingStatusIcon } from "../../lib/utils/getBlockingStatusIcon";
import useBlockingMode from "../../lib/hooks/useBlockingMode";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ACCOUNT_BILLS, MENU_ACCOUNT_BILLS_WRITE } from "../../lib/const/permissions";
import "./ProductsTable.less";

const styles = {
  buttonIcon: {
    boxShadow: "none",
    border: 0,
    marginLeft: "-10px",
    outline: "none",
    cursor: "pointer",
  },
};

interface Props {
  handleShowHistoryModal: (objId) => void;
  loadProductVersions: (props: { id: number }) => void;
  serviceListFilterValues: ServiceListFilterValues;
  handleSetServiceListFilterValues: (props: ServiceListFilterValuesChange) => void;
  handleShowBlockingModal: (obj) => void;
}

const columnsWidths = {
  extName: "620px",
  id: "120px",
  agreementNumber: "110px",
  offeringId: "100px",
  offeringName: "100px",
  productType: "143px",
  productGroup: "170px",
  status: "91px",
  fromDate: "130px",
  toDate: "130px",
};

const ProductsTable = ({
  handleShowHistoryModal,
  loadProductVersions,
  serviceListFilterValues,
  handleSetServiceListFilterValues,
  handleShowBlockingModal,
}: Props) => {
  const { serviceTypes } = useAppSelector((state) => state.service);
  const {
    productsList,
    productsListLoading,
    productVersions,
    productVersionsLoading,
  } = useAppSelector((state) => state.products);
  const { products, productsLoading, serviceStatus } = useAppSelector(
    (state) => state.rtkDictionaries
  );

  const isBlockingMode = useBlockingMode();

  const [filterIdsForm] = Form.useForm();
  const [activeIds, setActiveIds] = useState<number[]>([]);

  const [serviceData, setServiceData] = useState<IProductsTable[]>([]);
  const [serviceVersionData, setServiceVersionData] = useState<IProductsVersionsTable[]>([]);

  const [onExpandFunctionsForKey, setOnExpandFunctionsForKey] = useState<{
    [key: string]: (e) => void;
  }>({});

  const changeExpandFunctionsForKey = (callBack, record) => {
    if (!onExpandFunctionsForKey[record.key]) {
      setOnExpandFunctionsForKey((prev) => ({
        ...prev,
        [record.key]: (e) => {
          callBack(record, e);
        },
      }));
    }
  };

  const { agreementsLoading, agreements } = useAppSelector((state) => state.agreements);
  const { url } = useRouteMatch();
  const history = useHistory();

  const isDataForColumnsLoaded = checkIsLoaded(agreementsLoading, productsLoading);

  const [settings] = useState({
    fullDate: {
      name: "Полная дата",
      value: true,
    },
  });

  const dateNowParse = toHttpFormat(moment());

  const formatItemForProductsTable = (productsList: IProduct[]): IProductsTable[] => {
    if (productsList.length === 0) return [];

    return productsList.map((item) => {
      // В рамках задачи 83356 аналитики решили, что нужно добавлять первую версию продукта в иерархию;
      const sortedChildren = item?.children?.length
        ? [...item.children].sort((a, b) => sortByToDate(a, b, "ASC"))
        : [];
      const childrens: IProductsTable[] = [];
      sortedChildren.forEach((child) => {
        const childrenIds = childrens.map(({ id }) => id);
        if (!childrenIds.includes(child.id)) {
          childrens.push(formatItemForProductsTable([child])[0]);
        }
      });
      return {
        ...item,
        key: uniqueId(),
        extName: item.offering.extName,
        agreementNumber:
          item.agreement?.agreementNumber || item.offering?.agreement?.agreementNumber || "",
        offeringId: item.offering.id,
        offeringName: item.offering.name,
        productType: (serviceTypes as { id: number; name: string }[]).find(
          ({ id }) => id === item.serviceType?.id
        )?.name,
        productGroup: (products as { id: number; name: string }[]).find(({ id }) => {
          return id === item.serviceType?.productId;
        })?.name,
        status: (serviceStatus as { id: number; name: string }[]).find(
          ({ id: serviceStatusId }) => String(serviceStatusId) === String(item.serviceStatusId)
        )?.name,
        documentButton: true,
        children: [],
        childrens,
        isVersion: false,
        blockingStatus: item.blockingStatus,
        // Для модалки доп поля
        name: item.serviceType.name,
        agreementId: item.agreement.id,
      };
    });
  };

  useEffect(() => {
    if (productsList && productsList.length !== 0) {
      setServiceData(
        formatItemForProductsTable(productsList)
          .filter((productVersion) => {
            if (serviceListFilterValues.extName) {
              return String(productVersion?.extName)
                .toLowerCase()
                .includes(String(serviceListFilterValues.extName).toLowerCase());
            }
            return productVersion;
          })
          .filter(({ id }) => {
            if (activeIds.length === 0) {
              return true;
            }
            return activeIds.some((activeId) => String(id).startsWith(String(activeId)));
          })
          .filter((productVersion) => {
            if (serviceListFilterValues.activeAgreementId) {
              return productVersion.agreement.id === serviceListFilterValues.activeAgreementId;
            }
            return true;
          })
          .filter((productVersion) => {
            if (serviceListFilterValues.offeringName) {
              return String(productVersion?.offeringName)
                .toLowerCase()
                .includes(String(serviceListFilterValues.offeringName).toLowerCase());
            }
            return productVersion;
          })
          .filter((productVersion) => {
            if (serviceListFilterValues.productType) {
              return String(productVersion?.productType)
                .toLowerCase()
                .includes(String(serviceListFilterValues.productType).toLowerCase());
            }
            return productVersion;
          })
          .filter((productVersion) => {
            if (serviceListFilterValues.productGroup) {
              return String(productVersion?.productGroup)
                .toLowerCase()
                .includes(String(serviceListFilterValues.productGroup).toLowerCase());
            }
            return productVersion;
          })
      );
    }
    if (productsList.length === 0) {
      setServiceData([]);
    }
  }, [productsList, serviceListFilterValues, products, serviceTypes, serviceStatus, activeIds]);

  useEffect(() => {
    if (checkIsLoaded(productVersionsLoading)) {
      const tableData = productVersions.map((item) => {
        return {
          ...item,
          key: uniqueId(),
          extName: item.offering.extName,
          agreementNumber: item.agreement.agreementNumber,
          offeringId: item.offering.id,
          offeringName: item.offering.name,
          productType: (serviceTypes as { id: number; name: string }[]).find(
            ({ id }) => id === item.serviceType?.id
          )?.name,
          productGroup: (products as { id: number; name: string }[]).find(({ id }) => {
            return id === item.serviceType?.productId;
          })?.name,
          status: (serviceStatus as { id: number; name: string }[]).find(
            ({ id: serviceStatusId }) => String(serviceStatusId) === String(item.serviceStatusId)
          )?.name,
          documentButton: true,
          children: [],
          isVersion: true,
          isCurrentVersion: moment().isBetween(item.fromDate, item.toDate),
          isPastVersion: moment(item.fromDate).isBefore(dateNowParse),
          isFutureVersion: moment(item.fromDate).isAfter(dateNowParse),
        };
      });
      setServiceVersionData(tableData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productVersionsLoading, products, serviceTypes, serviceStatus]);

  const onProductExpand = (record) => {
    loadProductVersions(record);
  };

  const onRow = (record) => ({
    onClick: () => {
      if (!record.fromDate) {
        return console.error("У продукта отсуствует дата создания (fromDate)");
      }
      const { isSearchIncludePastDates } = serviceListFilterValues;

      if (isSearchIncludePastDates) {
        history.push(`${url}/${record.id}/search?onDate=${record.fromDate}`);
      } else {
        const fromDate = serviceListFilterValues.onDate.isBetween(record.fromDate, record.toDate)
          ? toHttpFormat(serviceListFilterValues.onDate)
          : record.fromDate;

        history.push(`${url}/${record.id}/search?onDate=${fromDate}`);
      }
    },
  });

  const renderExtNameColumnTitle = () => (
    <Input
      onChange={(e) => handleSetServiceListFilterValues({ extName: e.target.value })}
      value={serviceListFilterValues.extName}
      placeholder="Наименование предложения"
      style={{ width: "100%" }}
      allowClear
    />
  );

  renderExtNameColumnTitle.toString = () => "Наименование предложения";

  const renderIdTitle = () => {
    const notValidRgx = /[^0-9,]/;
    let valueTrim = "";
    const onChangeId = ({ target: { value } }) => {
      valueTrim = value.replace(/^\s+/g, "");
      filterIdsForm.setFieldsValue({ ids: valueTrim });
      if (!notValidRgx.test(value)) {
        const activeIdes = valueTrim?.split(/\D/).filter(Boolean).map(Number);
        setActiveIds(activeIdes);
      }
    };

    return (
      <Tooltip title="Введите ID через ','">
        <Form form={filterIdsForm}>
          <Form.Item
            name="ids"
            style={{ marginBottom: 0 }}
            className="form-item-required__hidden-error__mb0"
            rules={[
              {
                validator: (x, value) => {
                  if (value && /[^0-9,]/.test(value)) {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              aria-autocomplete="none"
              placeholder="ID"
              allowClear
              className="table-head-filter-search"
              suffix={searchIconForInput()}
              onChange={onChangeId}
            />
          </Form.Item>
        </Form>
      </Tooltip>
    );
  };

  renderIdTitle.toString = () => "ID";

  const renderOfferingNameColumnTitle = () => (
    <Input
      onChange={(e) => handleSetServiceListFilterValues({ offeringName: e.target.value })}
      value={serviceListFilterValues.offeringName}
      placeholder="Код"
      style={{ width: "100%" }}
      allowClear
    />
  );

  renderOfferingNameColumnTitle.toString = () => "Код";

  const renderProductTypeColumnTitle = () => (
    <Input
      onChange={(e) => handleSetServiceListFilterValues({ productType: e.target.value })}
      value={serviceListFilterValues.productType}
      placeholder="Тип продукта"
      style={{ width: "100%" }}
      allowClear
    />
  );

  const renderIdAgreementTitle = () => {
    return (
      <Select
        onChange={(e) => handleSetServiceListFilterValues({ activeAgreementId: e })}
        value={serviceListFilterValues.activeAgreementId}
        style={{ width: "100%" }}
        placeholder="Договор"
        allowClear
        showSearch
        suffixIcon={searchIconForInput()}
        className="renderIdAgreementTitle"
        filterOption={(inputValue, options) =>
          options?.children?.toLowerCase().includes(inputValue.toLowerCase())
        }
      >
        {agreements.map(({ id, agreementNumber }) => (
          <Select.Option key={uuid()} value={id}>
            {agreementNumber}
          </Select.Option>
        ))}
      </Select>
    );
  };

  renderIdAgreementTitle.toString = () => "Договор";

  renderProductTypeColumnTitle.toString = () => "Тип продукта";

  const renderProductGroupColumnTitle = () => (
    <Input
      onChange={(e) => handleSetServiceListFilterValues({ productGroup: e.target.value })}
      value={serviceListFilterValues.productGroup}
      placeholder="Группа продуктов"
      style={{ width: "100%" }}
      allowClear
      title="Группа продуктов"
    />
  );

  renderProductGroupColumnTitle.toString = () => "Группа продуктов";

  const [isVersionsOpen, setIsVersionsOpen] = useState<{
    id?: number;
    visible: boolean;
    key?: string | number;
  }>({
    visible: false,
  });

  const onClickVersionIcon = (rec, e) => {
    if (isVersionsOpen.visible && rec.id === isVersionsOpen.id) {
      setIsVersionsOpen({ visible: false });
    } else {
      setIsVersionsOpen({ id: rec.id, visible: true, key: rec.key });
      onProductExpand(rec);
      onExpandFunctionsForKey[rec.key] && onExpandFunctionsForKey[rec.key](e);
    }
  };

  const getColumns = () => {
    const getDateValues = (value) => {
      const valueMs = moment(value).millisecond().toString();
      const zeroStr = "0".repeat(3 - valueMs.length);
      const date = moment(value).format("DD.MM.YYYY");
      const time = moment(value).format("HH:mm:ss");
      return {
        date,
        time: `${time}.${zeroStr}${valueMs}`,
      };
    };
    const columns: TableColumn<IProductsTable | IProductsVersionsTable>[] = [
      {
        title: renderExtNameColumnTitle,
        dataIndex: "extName",
        key: "extName",
        render(value) {
          return (
            <Tooltip title={value}>
              <div
                style={{
                  maxWidth: "260px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <span>{value}</span>
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: "",
        dataIndex: "blockingStatus",
        key: "blockingStatus",
        width: "50px",
        render: (blockingStatus, record) => (
          <div>{getBlockingStatusIcon(blockingStatus, () => handleShowBlockingModal(record))}</div>
        ),
      },
      {
        title: renderIdTitle,
        dataIndex: "id",
        key: "id",
        width: columnsWidths.id,
        render: (id) => (
          <span style={{ color: "rgba(0, 0, 0, 0.3)", fontWeight: "normal" }}>{id}</span>
        ),
        hidden: true,
      },
      {
        title: renderIdAgreementTitle,
        dataIndex: "agreementNumber",
        key: "agreementNumber",
        width: columnsWidths.agreementNumber,
        render: (agreementNumber) => <span>{agreementNumber}</span>,
      },
      {
        title: "ID предложения",
        dataIndex: "offeringId",
        key: "offeringId",
        width: columnsWidths.offeringId,
        hidden: true,
      },
      {
        title: renderOfferingNameColumnTitle,
        dataIndex: "offeringName",
        key: "offeringName",
        width: columnsWidths.offeringName,
        render: (offeringName) => <span style={{ wordBreak: "break-all" }}>{offeringName}</span>,
      },
      {
        title: renderProductTypeColumnTitle,
        dataIndex: "productType",
        key: "productType",
        width: columnsWidths.productType,
      },
      {
        title: renderProductGroupColumnTitle,
        dataIndex: "productGroup",
        key: "productGroup",
        width: columnsWidths.productGroup,
      },
      {
        title: "Статус",
        dataIndex: "status",
        key: "status",
        width: columnsWidths.status,
        render: (status) => <span style={{ wordBreak: "break-all" }}>{status}</span>,
      },
      {
        title: "Действительна с",
        dataIndex: "fromDate",
        key: "fromDate",
        width: columnsWidths.fromDate,
        sortOrder:
          "fromDate" in serviceListFilterValues.currentSortOrder
            ? serviceListFilterValues.currentSortOrder.fromDate
            : null,
        sorter: (a, b) => moment(a.fromDate).unix() - moment(b.fromDate).unix(),
        render: (text) => {
          if (!text) return null;
          const { date, time } = getDateValues(text);
          return (
            <>
              <span>{date} </span>
              {settings.fullDate.value && (
                <>
                  <br />
                  <span style={{ fontSize: "11px" }}>{time}</span>
                </>
              )}
            </>
          );
        },
      },
      {
        title: "Действительна по",
        dataIndex: "toDate",
        key: "toDate",
        width: columnsWidths.toDate,
        sortOrder:
          "toDate" in serviceListFilterValues.currentSortOrder
            ? serviceListFilterValues.currentSortOrder.toDate
            : null,
        sorter: (a, b) => moment(a.toDate).unix() - moment(b.toDate).unix(),
        render: (text) => {
          if (!text) return null;
          const { date, time } = getDateValues(text);
          return (
            <>
              <span>{date} </span>
              {settings.fullDate.value && (
                <>
                  <br />
                  <span style={{ fontSize: "11px" }}>{time}</span>
                </>
              )}
            </>
          );
        },
      },
      {
        dataIndex: "documentButton",
        key: "documentButton",
        width: "80px",
        render: (_v, rec) => {
          const onClick = (e) => {
            e.stopPropagation();
            const link = url.replace("products", "bills");
            history.push(`${link}/search?serviceId=${rec.id}`);
          };
          return (
            <Row style={{ minWidth: 60, maxWidth: 60 }} align="middle" wrap={false}>
              <Col span={12}>
                <>
                  {hasAccess(MENU_ACCOUNT_BILLS, MENU_ACCOUNT_BILLS_WRITE) && (
                    <Tooltip title="Показать счета по продукту">
                      <button type="button" className="button-icon" onClick={onClick}>
                        <img
                          src={documentList}
                          alt="documentList"
                          width="24px"
                          height="24px"
                          style={styles.buttonIcon}
                        />
                      </button>
                    </Tooltip>
                  )}
                </>
              </Col>
              {!rec?.isVersion && (
                <Col span={12}>
                  <Button
                    icon={
                      isVersionsOpen.visible && isVersionsOpen.id === rec.id ? (
                        <VersionsShownIcon />
                      ) : (
                        <VersionsHiddenIcon />
                      )
                    }
                    type="text"
                    className="button-icon destination-view-version-icon"
                    htmlType="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClickVersionIcon(rec, e);
                    }}
                  />
                </Col>
              )}
            </Row>
          );
        },
      },
      {
        dataIndex: "btns",
        key: "btns",
        width: "42px",
        style: { padding: "10px" },
        render: (v, rec) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleShowHistoryModal(rec);
            }}
            className="button-icon"
            style={styles.buttonIcon}
            type="button"
          >
            <img src={dots} alt="dots" />
          </button>
        ),
      },
    ];

    return columns.filter(({ key }) => {
      return !(key === "blockingStatus" && !isBlockingMode);
    });
  };

  const [columns, setColumns] = useState<TableColumn<IProductsTable>[]>([]);

  useEffect(() => {
    if (isDataForColumnsLoaded && isEmpty(columns)) {
      setColumns(getColumns());
    }
  }, [isDataForColumnsLoaded]);

  useEffect(() => {
    setColumns(getColumns());
  }, [isBlockingMode, agreements, serviceListFilterValues]);

  // Изменение колонок таблицы только в части рендера с сохранением остального (например скрытия колонок)
  useEffect(() => {
    if (!isEmpty(columns)) {
      const newColumns = getColumns();
      setColumns((prev) =>
        prev.map((column) => {
          const newColumn = newColumns.find(({ key }) => column.key === key);
          if (column.render || newColumn?.render) {
            return {
              ...column,
              render: newColumn?.render,
            };
          }
          return column;
        })
      );
    }
  }, [settings, isVersionsOpen, serviceVersionData, onExpandFunctionsForKey]);

  // Изменение колонок таблицы только в части порядка сортировки с сохранением остального (например скрытия колонок)
  useEffect(() => {
    if (!isEmpty(columns)) {
      const newColumns = getColumns();
      setColumns((prev) =>
        prev.map((column) => {
          const newColumn = newColumns.find(({ key }) => column.key === key);
          if (column.sortOrder || newColumn?.sortOrder) {
            return {
              ...column,
              sortOrder: newColumn?.sortOrder,
            };
          }
          return column;
        })
      );
    }
  }, [serviceListFilterValues.currentSortOrder]);

  const innerRowClassName = (record) => {
    if (record.isVersion) {
      if (record.isCurrentVersion) {
        return `row_child row_current-version`;
      }
      if (record.isPastVersion) {
        return "row_child row_past-version";
      }
      return "row_child";
    }
    return "";
  };

  const columnsTable = [...columns].filter((item) => !item.hidden);

  columnsTable[0] = { ...columnsTable[0], width: undefined };

  const columnsTableChildren = columnsTable.map((el) => {
    if (el.key === "blockingStatus") {
      return {
        title: "",
        dataIndex: "blockingStatus",
        key: "blockingStatus",
        width: "50px",
        sortOrder: undefined,
        render: (blockingStatus, record) => (
          <div>{getBlockingStatusIcon(blockingStatus, () => handleShowBlockingModal(record))}</div>
        ),
      };
    }
    return { ...el, sortOrder: undefined };
  });

  const expandedRowRender = (
    record: IProductsTable | IProductsVersionsTable,
    depth: number,
    depthP: number,
    depthChild
  ) => {
    let listVisibleVersions = record?.childrens;
    if (isVersionsOpen.visible && isVersionsOpen.id === record.id) {
      listVisibleVersions = [...serviceVersionData];
      if (record?.childrens) {
        listVisibleVersions.push(...record?.childrens);
      }
    }

    return (
      <Table
        columns={columnsTableChildren}
        dataSource={listVisibleVersions}
        rowClassName={innerRowClassName}
        size="small"
        pagination={false}
        onRow={onRow}
        showHeader={false}
        expandable={{
          indentSize: 0,
          expandedRowRender: (recordRow) => {
            // Проверка есть ли дети иерархии и что у родителя это последний элемент
            const isChildNoMore =
              recordRow?.childrens &&
              recordRow?.childrens.length === 1 &&
              record?.childrens &&
              record?.childrens[record.childrens?.length - 1]?.id === recordRow?.id;

            // Рекурсивно вызываем таблицу для создания иерархии
            return expandedRowRender(
              recordRow,
              depth + 4.7,
              depthP + 1,
              // Генерируем для следующего уровня проверку, нужно ли ему отображать пунктирную линию предыдущего уровня и всех до него
              isChildNoMore
                ? { ...depthChild, [depthP]: false }
                : {
                    ...depthChild,
                    [depthP]:
                      record?.childrens &&
                      record?.childrens[record?.childrens.length - 1].id === recordRow.id
                        ? false
                        : depthChild[depthP],
                    [depthP + 1]: true,
                  }
            );
          },
          rowExpandable: (recordRow) => {
            // Расширяемая таблица только для строк у которых есть иерархия тоесть кнопки или если просматриваем версии этой строки
            return (
              (!!recordRow?.childrens && recordRow?.childrens?.length > 0) ||
              recordRow.id === isVersionsOpen?.id
            );
          },
          expandIcon: ({ expanded, onExpand, record: recordRow }) => {
            // Записываем в состояние все функции открытия, так как по другому её не достать для вызова вне expandable функции
            changeExpandFunctionsForKey(onExpand, recordRow);

            // Генерируем пустой массив и его дляну = (количество пунктирных линий которые нам надо отлировать по порядку)
            const arr: string[] = [];
            arr.length = depthP - 1;
            arr.fill("");

            // Условие для строк где есть кнопки + -
            if (!!recordRow?.childrens && recordRow?.childrens?.length > 0) {
              return expanded ? (
                <>
                  <Row
                    wrap={false}
                    style={{
                      marginLeft: `${9 * depth}px`,
                      position: "absolute",
                      top: 0,
                      width: 34,
                      height: "50%",
                      borderLeft: "1px dashed #AFB5C1",
                      borderBottom: !recordRow.isVersion ? "1px dashed #AFB5C1" : "",
                    }}
                  >
                    <button
                      style={{ left: "100%", position: "absolute", bottom: "-9px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onExpand(recordRow, e);
                      }}
                      className="ant-table-row-expand-icon ant-table-row-expand-icon-expanded"
                      type="button"
                    />
                  </Row>
                  {arr.map((item, index) => {
                    if (!depthChild[index + 1]) {
                      return null;
                    }
                    return (
                      <Row
                        key={index}
                        wrap={false}
                        style={{
                          marginLeft: `${9 * (index + 1) + (index > 0 ? 33.3 * index : 0)}px`,
                          position: "absolute",
                          top: 0,
                          width: 34,
                          height: "100%",
                          borderLeft: "1px dashed #AFB5C1",
                          zIndex: 2,
                        }}
                      />
                    );
                  })}
                  {record?.childrens &&
                    record?.childrens[record?.childrens?.length - 1].id !== recordRow.id && (
                      <Row
                        wrap={false}
                        style={{
                          marginLeft: `${9 * depth}px`,
                          position: "absolute",
                          top: 0,
                          width: 34,
                          height: "100%",
                          borderLeft: "1px dashed #AFB5C1",
                          zIndex: 1,
                        }}
                      />
                    )}
                  <div
                    style={{
                      width: `${9 * depth + 50}px`,
                    }}
                  />
                  {!record.isVersion && (
                    <div
                      style={{
                        width: `${9 * depth + 43.3}px`,
                        position: "absolute",
                        height: "calc(50% - 8px)",
                        borderRight: "1px dashed #AFB5C1",
                        bottom: 0,
                      }}
                    />
                  )}
                </>
              ) : (
                <>
                  <Row
                    wrap={false}
                    style={{
                      marginLeft: `${9 * depth}px`,
                      position: "absolute",
                      top: 0,
                      width: 34,
                      height: "50%",
                      borderLeft: "1px dashed #AFB5C1",
                      borderBottom: !recordRow.isVersion ? "1px dashed #AFB5C1" : "",
                    }}
                  >
                    <button
                      style={{ left: "100%", position: "absolute", bottom: "-9px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onExpand(recordRow, e);
                      }}
                      className="ant-table-row-expand-icon ant-table-row-expand-icon-collapsed"
                      type="button"
                    />
                  </Row>

                  {arr.map((item, index) => {
                    if (!depthChild[index + 1]) {
                      return null;
                    }
                    return (
                      <Row
                        key={index}
                        wrap={false}
                        style={{
                          marginLeft: `${9 * (index + 1) + (index > 0 ? 33.3 * index : 0)}px`,
                          position: "absolute",
                          top: 0,
                          width: 34,
                          height: "100%",
                          borderLeft: "1px dashed #AFB5C1",
                          zIndex: 2,
                        }}
                      />
                    );
                  })}

                  {record?.childrens &&
                    record?.childrens[record?.childrens?.length - 1].id !== recordRow.id && (
                      <Row
                        wrap={false}
                        style={{
                          marginLeft: `${9 * depth}px`,
                          position: "absolute",
                          top: 0,
                          width: 34,
                          height: "100%",
                          borderLeft: "1px dashed #AFB5C1",
                          zIndex: 1,
                        }}
                      />
                    )}
                  <div
                    style={{
                      width: `${9 * depth + 50}px`,
                    }}
                  />
                </>
              );
            }
            // Условия для строк без кнопок + -
            return (
              <>
                {(recordRow.isVersion ? record.childrens?.length !== 0 : true) && (
                  <Row
                    wrap={false}
                    style={{
                      marginLeft: `${9 * depth}px`,
                      position: "absolute",
                      top: 0,
                      width: 34,
                      height: "50%",
                      borderLeft: "1px dashed #AFB5C1",
                      borderBottom: !recordRow.isVersion ? "1px dashed #AFB5C1" : "",
                    }}
                  />
                )}

                {arr.map((_, index) => {
                  if (!depthChild[index + 1]) {
                    return null;
                  }
                  return (
                    <Row
                      key={index}
                      wrap={false}
                      style={{
                        marginLeft: `${9 * (index + 1) + (index > 0 ? 33.3 * index : 0)}px`,
                        position: "absolute",
                        top: 0,
                        width: 34,
                        height: "100%",
                        borderLeft: "1px dashed #AFB5C1",
                        zIndex: 2,
                      }}
                    />
                  );
                })}

                {record?.childrens &&
                  record?.childrens[record?.childrens?.length - 1]?.id !== recordRow.id &&
                  (recordRow.isVersion ? record.childrens.length !== 0 : true) && (
                    <Row
                      wrap={false}
                      style={{
                        marginLeft: `${9 * depth}px`,
                        position: "absolute",
                        top: 0,
                        width: 34,
                        height: "100%",
                        borderLeft: "1px dashed #AFB5C1",
                        zIndex: 1,
                      }}
                    />
                  )}
                {recordRow.isVersion ? (
                  <div
                    style={{
                      width: `${9 * depth + 34}px`,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: `${9 * depth + 34}px`,
                    }}
                  />
                )}
              </>
            );
          },
        }}
      />
    );
  };

  return (
    <div className="product-list-table">
      <Row justify="end" style={{ marginTop: "10px" }}>
        <ToggleColumns tableName="products-table" columns={columns} setColumns={setColumns} />
      </Row>
      <Table
        columns={columnsTable}
        dataSource={serviceData}
        rowClassName={innerRowClassName}
        size="small"
        pagination={false}
        scroll={{
          x: "1750px",
          // y: "calc(100vh - 280px)",
        }}
        onChange={(...props) => {
          const [, , sortProps] = props;
          const { field, order } = sortProps as {
            field: "fromDate" | "toDate";
            order?: "ascend" | "descend";
          };
          handleSetServiceListFilterValues({
            currentSortOrder: { [field]: order },
          });
        }}
        loading={!isDataForColumnsLoaded || isEmpty(columns) || checkIsLoading(productsListLoading)}
        onRow={onRow}
        expandable={{
          indentSize: 0,
          expandedRowRender: (record) =>
            expandedRowRender(
              record,
              1,
              1,
              record?.childrens && record?.childrens.length === 1 ? {} : { 1: true }
            ),
          rowExpandable: (recordRow) => {
            // Расширяемая таблица только для строк у которых есть иерархия тоесть кнопки или если просматриваем версии этой строки
            return (
              (!!recordRow?.childrens && recordRow?.childrens?.length > 0) ||
              recordRow.id === isVersionsOpen?.id
            );
          },
          expandIcon: ({ expanded, onExpand, record }) => {
            changeExpandFunctionsForKey(onExpand, record);

            if (!!record?.childrens && record?.childrens?.length > 0) {
              return expanded ? (
                <>
                  <Row>
                    <button
                      style={{ marginLeft: 0 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onExpand(record, e);
                      }}
                      className="ant-table-row-expand-icon ant-table-row-expand-icon-expanded"
                      type="button"
                    />
                  </Row>
                  <div
                    style={{
                      width: `${10}px`,
                      position: "absolute",
                      height: "calc(50% - 8px)",
                      borderRight: "1px dashed #AFB5C1",
                      bottom: 0,
                    }}
                  />
                </>
              ) : (
                <Row>
                  <button
                    style={{ marginLeft: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpand(record, e);
                    }}
                    className="ant-table-row-expand-icon ant-table-row-expand-icon-collapsed"
                    type="button"
                  />
                </Row>
              );
            }
            return null;
          },
        }}
      />
    </div>
  );
};

export default ProductsTable;
